import { useState } from "react";
import {
  makeEconomy, upsertFuelLog, deleteFuelLog, newId,
  type CanonicalStore, type Car, type FuelLog,
} from "../../core/index.ts";
import { Modal, TextField, NumberField, SelectField, DateField, CheckboxField, FormActions, parseNum, numToStr } from "./Modal.tsx";
import { PhotoField, DocField } from "./Attachments.tsx";
import { epochToDateInput, dateInputToEpoch } from "../format.ts";

const GRADES = ["Regular 95", "Premium 98", "Super 100", "Diesel", "Premium Diesel", "LPG", "Electric"];

export function FuelLogForm({ store, car, editing, onSave, onClose }: {
  store: CanonicalStore; car: Car; editing?: FuelLog; onSave: (s: CanonicalStore) => void; onClose: () => void;
}) {
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const ft = (car.fuelType ?? "").toLowerCase();
  const isEV = ft === "electric";
  const isPHEV = !isEV && (ft.includes("plug-in") || ((car.tankVolume ?? 0) > 0 && (car.batteryCapacity ?? 0) > 0));

  const [odometer, setOdo] = useState(editing ? numToStr(round1(economy.convertDistance(editing.odometer))) : "");
  const [volume, setVol] = useState(editing && editing.volumeAdded > 0 ? numToStr(round1(economy.convertVolume(editing.volumeAdded))) : "");
  const [energy, setEnergy] = useState(editing?.kwhAdded ? numToStr(editing.kwhAdded) : "");
  const [pricePerUnit, setPricePerUnit] = useState(editing?.fuelCost != null && editing.volumeAdded > 0 ? numToStr(editing.fuelCost / economy.convertVolume(editing.volumeAdded)) : "");
  const [pricePerKwh, setPricePerKwh] = useState(editing?.energyCost != null && editing.kwhAdded ? numToStr(editing.energyCost / editing.kwhAdded) : "");
  const [total, setTotal] = useState(editing ? numToStr(editing.totalCost) : "");
  const [fuelTotal, setFuelTotal] = useState(editing?.fuelCost != null ? numToStr(editing.fuelCost) : "");
  const [chargeTotal, setChargeTotal] = useState(editing?.energyCost != null ? numToStr(editing.energyCost) : "");
  const [grade, setGrade] = useState(editing?.fuelGrade ?? (isEV ? "Electric" : "Regular 95"));
  const [station, setStation] = useState(editing?.stationName ?? "");
  const [dateStr, setDate] = useState(epochToDateInput(editing?.date ?? null));
  const [fullFill, setFull] = useState(editing?.isFullFill ?? true);
  const [avgSpeed, setSpeed] = useState(editing?.avgSpeed ? numToStr(editing.avgSpeed) : "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [photos, setPhotos] = useState<string[]>(editing?.photoFilenames ?? []);
  const [docs, setDocs] = useState<string[]>(editing?.documentFilenames ?? []);

  const currency = store.profile.displayCurrency;
  const vl = economy.volumeLabel();

  // Auto-calculate totals from price per unit
  const calcFuelTotal = pricePerUnit !== "" && volume !== "" ? parseNum(pricePerUnit) * parseNum(volume) : 0;
  const calcChargeTotal = pricePerKwh !== "" && energy !== "" ? parseNum(pricePerKwh) * parseNum(energy) : 0;

  // Use manual total if set, otherwise use calculated
  const effectiveFuelTotal = fuelTotal !== "" ? parseNum(fuelTotal) : calcFuelTotal;
  const effectiveChargeTotal = chargeTotal !== "" ? parseNum(chargeTotal) : calcChargeTotal;

  function build(): FuelLog {
    const odoKm = economy.toKm(parseNum(odometer));
    let vol = 0, kwh: number | null = null, totalCost = 0, fuelCost: number | null = null, energyCost: number | null = null;
    if (isPHEV) {
      vol = volume ? economy.toLitres(parseNum(volume)) : 0;
      kwh = energy ? parseNum(energy) : null;
      fuelCost = vol > 0 ? effectiveFuelTotal : null;
      energyCost = kwh != null ? effectiveChargeTotal : null;
      totalCost = (fuelCost ?? 0) + (energyCost ?? 0);
    } else if (isEV) {
      kwh = parseNum(energy);
      totalCost = parseNum(total);
    } else {
      vol = economy.toLitres(parseNum(volume));
      totalCost = parseNum(total);
      // If user entered price per unit, calculate total
      if (pricePerUnit !== "" && vol > 0) {
        totalCost = parseNum(pricePerUnit) * economy.convertVolume(vol);
      }
    }
    return {
      id: editing?.id ?? newId(),
      carId: car.id,
      date: dateInputToEpoch(dateStr),
      odometer: odoKm,
      volumeAdded: vol,
      kwhAdded: kwh,
      solarKwh: editing?.solarKwh ?? null,
      solarPrice: editing?.solarPrice ?? null,
      totalCost,
      fuelCost,
      energyCost,
      currency,
      fuelGrade: isEV ? "Electric" : grade,
      isFullFill: fullFill,
      stationName: station.trim() || null,
      notes: notes.trim() || null,
      avgSpeed: avgSpeed ? parseNum(avgSpeed) : null,
      photoFilenames: photos,
      documentFilenames: docs,
    };
  }

  const canSave = odometer !== "" && (isPHEV ? (effectiveFuelTotal > 0 || effectiveChargeTotal > 0) : parseNum(total) > 0 || (pricePerUnit !== "" && parseNum(volume) > 0) || (pricePerKwh !== "" && parseNum(energy) > 0));
  const dl = economy.distanceLabel();

  const inp: React.CSSProperties = {
    width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
    color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px",
    boxSizing: "border-box",
  };

  return (
    <Modal title={editing ? "Edit entry" : isEV ? "New charge" : "New fill-up"} onClose={onClose}>
      <NumberField label={`Odometer (${dl})`} value={odometer} onChange={setOdo} />
      {isPHEV ? (
        <>
          <NumberField label={`Volume (${vl})`} value={volume} onChange={setVol} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PRICE PER {vl.toUpperCase()}</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={pricePerUnit} onChange={(e) => setPricePerUnit(e.target.value)} />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>FUEL TOTAL ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={fuelTotal || (calcFuelTotal > 0 ? calcFuelTotal.toFixed(2) : "")} onChange={(e) => setFuelTotal(e.target.value)} />
            </div>
          </div>
          <NumberField label="Energy (kWh)" value={energy} onChange={setEnergy} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PRICE PER kWh ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={pricePerKwh} onChange={(e) => setPricePerKwh(e.target.value)} />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>CHARGE TOTAL ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={chargeTotal || (calcChargeTotal > 0 ? calcChargeTotal.toFixed(2) : "")} onChange={(e) => setChargeTotal(e.target.value)} />
            </div>
          </div>
          <SelectField label="Fuel grade" value={grade} onChange={setGrade} options={GRADES.filter((g) => g !== "Electric")} />
        </>
      ) : isEV ? (
        <>
          <NumberField label="Energy added (kWh)" value={energy} onChange={setEnergy} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PRICE PER kWh ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={pricePerKwh} onChange={(e) => setPricePerKwh(e.target.value)} />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>CHARGE TOTAL ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={total || (calcChargeTotal > 0 ? calcChargeTotal.toFixed(2) : "")} onChange={(e) => setTotal(e.target.value)} />
            </div>
          </div>
        </>
      ) : (
        <>
          <NumberField label={`Volume (${vl})`} value={volume} onChange={setVol} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PRICE PER {vl.toUpperCase()}</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={pricePerUnit} onChange={(e) => setPricePerUnit(e.target.value)} />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>FUEL TOTAL ({currency})</label>
              <input style={inp} type="number" step="0.01" placeholder="0.00" value={total || (calcFuelTotal > 0 ? calcFuelTotal.toFixed(2) : "")} onChange={(e) => setTotal(e.target.value)} />
            </div>
          </div>
          <SelectField label="Fuel grade" value={grade} onChange={setGrade} options={GRADES.filter((g) => g !== "Electric")} />
        </>
      )}
      <DateField label="Date" value={dateStr} onChange={setDate} />
      <CheckboxField label="Full fill" checked={fullFill} onChange={setFull} />
      <TextField label="Station (optional)" value={station} onChange={setStation} />
      <NumberField label={`Avg speed (${dl}/h, optional)`} value={avgSpeed} onChange={setSpeed} />
      <TextField label="Notes (optional)" value={notes} onChange={setNotes} />
      <PhotoField label="Photos (receipt, pump…)" filenames={photos} onChange={setPhotos} />
      <DocField label="Documents" filenames={docs} onChange={setDocs} />
      <FormActions
        canSave={canSave}
        onSave={() => { onSave(upsertFuelLog(store, build())); onClose(); }}
        onDelete={editing ? () => { onSave(deleteFuelLog(store, editing.id)); onClose(); } : undefined}
      />
    </Modal>
  );
}

const round1 = (v: number) => Math.round(v * 10) / 10;
